import React, { useEffect, useRef } from 'react';

// The writings as a quiet price line: time runs left to right, each post is a fill on it.
// Hovering a post (here or in the list) marks it. Colours come from the page's CSS tokens.
const START = new Date('2022-09-01').getTime();
const H = 150, PAD = { top: 12, right: 8, bottom: 24, left: 8 };

// Same line on every visit: a seeded walk with a step up at each post.
function makeSeries(posts, end, n = 220) {
  let s = 7;
  const rnd = () => ((s = (s * 9301 + 49297) % 233280) / 233280);
  const times = posts.map((p) => new Date(p.date).getTime());
  const pts = [];
  let v = 0;
  for (let i = 0; i < n; i++) {
    const t = START + ((end - START) * i) / (n - 1);
    const prev = i ? pts[i - 1].t : START;
    v += (rnd() - 0.47) * 1.1;
    for (const pt of times) if (pt > prev && pt <= t) v += 4.5;
    pts.push({ t, v });
  }
  return pts;
}

function WritingsChart({ posts, active, setActive }) {
  const ref = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    const end = Date.now();
    const series = makeSeries(posts, end);
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const born = performance.now();
    let W = 0, raf = 0, markers = [];

    const lo = Math.min(...series.map((p) => p.v)), hi = Math.max(...series.map((p) => p.v));
    const x = (t) => PAD.left + ((t - START) / (end - START)) * (W - PAD.left - PAD.right);
    const y = (v) => PAD.top + (1 - (v - lo) / (hi - lo)) * (H - PAD.top - PAD.bottom);
    const valueAt = (t) => series[Math.max(0, series.findIndex((p) => p.t >= t))].v;

    const resize = () => {
      W = canvas.parentElement.clientWidth;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (now) => {
      const css = getComputedStyle(canvas);
      const INK = css.getPropertyValue('--ink').trim();
      const MUTED = css.getPropertyValue('--muted').trim();
      const HAIR = css.getPropertyValue('--hair').trim();
      const BG = css.getPropertyValue('--bg').trim();
      const ACCENT = css.getPropertyValue('--accent').trim();
      const age = now - born;
      const k = still ? 1 : Math.min(1, age / 1600);
      const shown = Math.max(2, Math.round(series.length * (1 - Math.pow(1 - k, 3))));
      ctx.clearRect(0, 0, W, H);

      // Baseline and year ticks.
      ctx.fillStyle = HAIR; ctx.fillRect(PAD.left, H - PAD.bottom, W - PAD.left - PAD.right, 1);
      ctx.font = '11px "Geist Mono", ui-monospace, monospace'; ctx.textBaseline = 'alphabetic';
      for (let yr = 2023; yr <= new Date(end).getFullYear(); yr++) {
        const tx = x(new Date(`${yr}-01-01`).getTime());
        ctx.fillStyle = HAIR; ctx.fillRect(tx, H - PAD.bottom, 1, 4);
        ctx.fillStyle = MUTED; ctx.fillText(String(yr), tx + 4, H - 6);
      }

      // The line.
      const pts = series.slice(0, shown);
      const last = pts[pts.length - 1];
      ctx.beginPath();
      pts.forEach((p, i) => (i ? ctx.lineTo(x(p.t), y(p.v)) : ctx.moveTo(x(p.t), y(p.v))));
      ctx.strokeStyle = ACCENT; ctx.lineWidth = 1.25; ctx.lineJoin = 'round'; ctx.stroke();

      // Today: a dot that breathes.
      const pulse = still ? 0 : (age % 2000) / 2000;
      ctx.beginPath(); ctx.arc(x(last.t), y(last.v), 2.5 + pulse * 7, 0, Math.PI * 2);
      ctx.globalAlpha = 0.18 * (1 - pulse); ctx.fillStyle = INK; ctx.fill(); ctx.globalAlpha = 1;
      ctx.beginPath(); ctx.arc(x(last.t), y(last.v), 2.5, 0, Math.PI * 2); ctx.fillStyle = ACCENT; ctx.fill();

      // A mark for each post the line has reached.
      markers = [];
      posts.forEach((p, i) => {
        const t = new Date(p.date).getTime();
        if (t > last.t) return;
        const mx = x(t), my = y(valueAt(t));
        markers.push({ i, mx, my });
        const on = activeRef.current === i;
        if (on) {
          ctx.fillStyle = HAIR; ctx.fillRect(Math.round(mx), my + 6, 1, H - PAD.bottom - my - 6);
        }
        ctx.beginPath(); ctx.arc(mx, my, on ? 4.5 : 3, 0, Math.PI * 2);
        ctx.fillStyle = on ? ACCENT : BG; ctx.fill();
        ctx.strokeStyle = on ? ACCENT : MUTED; ctx.lineWidth = 1.25; ctx.stroke();
      });

      raf = requestAnimationFrame(draw);
    };

    const hit = (e) => {
      const r = canvas.getBoundingClientRect();
      const px = e.clientX - r.left, py = e.clientY - r.top;
      let best = null, bd = 16;
      for (const m of markers) { const d = Math.hypot(m.mx - px, m.my - py); if (d < bd) { bd = d; best = m; } }
      return best;
    };
    const onMove = (e) => { const m = hit(e); canvas.style.cursor = m ? 'pointer' : 'default'; setActive(m ? m.i : null); };
    const onLeave = () => setActive(null);
    const onClick = (e) => { const m = hit(e); if (m) window.open(posts[m.i].href, '_blank', 'noopener'); };

    resize();
    raf = requestAnimationFrame(draw);
    window.addEventListener('resize', resize);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
  }, [posts, setActive]);

  return (
    <div className="chart">
      <canvas ref={ref} role="img" aria-label="Posts over time, drawn like a price chart" />
    </div>
  );
}

export default WritingsChart;
